import {Component, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {Router} from '@angular/router';
import {AppointmentsController} from '../appointments/service/appointments.controller';
import {AppointmentsFlowStateService} from '../../common/service/appointments-flow-state.service';
import {SmsCodeInputComponent} from '../appointments-shared/sms-code-input/sms-code-input.component';
import {ErrorBannerComponent} from '../appointments-shared/error-banner/error-banner.component';

@Component({
  selector: 'app-appointments-verification',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    SmsCodeInputComponent,
    ErrorBannerComponent
  ],
  templateUrl: './appointments-verification.component.html',
  styleUrls: ['./appointments-verification.component.scss']
})
export class AppointmentsVerificationComponent implements OnInit, OnDestroy {
  code = '';
  maskedPhone = '';
  infoMessage: string | null = null;
  errorMessage: string | null = null;
  rateLimitMessage: string | null = null;
  isVerifying = false;
  isSending = false;
  remainingSeconds = 0;
  private countdownTimer?: number;

  constructor(
    private controller: AppointmentsController,
    private flowState: AppointmentsFlowStateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const phone = this.flowState.snapshot.patientDetails?.phone ?? '';
    this.maskedPhone = this.maskPhone(phone);
    this.syncRateLimit(true);
  }

  ngOnDestroy(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }

  onCodeChange(code: string) {
    this.code = code.replace(/\D/g, '');
    this.errorMessage = null;
  }

  get canVerify(): boolean {
    return this.code.length === 6 && !this.isVerifying;
  }

  verify() {
    if (!this.canVerify) return;
    this.isVerifying = true;
    this.errorMessage = null;

    this.controller.verifySmsCode(this.code).subscribe({
      next: (res) => {
        this.isVerifying = false;
        if (res.status === 'verified') {
          this.flowState.markVerificationCompleted();
          this.controller.confirmBooking().subscribe({
            next: (confirmation) => {
              this.flowState.setBookingConfirmation(confirmation);
              this.router.navigate(['/erfolg']);
            },
            error: () => {
              this.errorMessage = 'Die Buchung konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.';
            }
          });
          return;
        }
        if (res.status === 'expired') {
          this.errorMessage = res.message ?? 'Der Code ist abgelaufen.';
          return;
        }
        if (res.status === 'invalid') {
          this.errorMessage = res.message ?? 'Der Code ist nicht korrekt.';
          return;
        }
        if (res.status === 'failed') {
          this.errorMessage = res.message ?? 'Technischer Fehler bei der Verifikation.';
        }
      },
      error: (err) => {
        this.isVerifying = false;
        this.errorMessage = err?.message ?? 'Technischer Fehler bei der Verifikation.';
      }
    });
  }

  sendCode() {
    if (this.isSending || this.isRateLimited) return;
    this.isSending = true;
    this.infoMessage = null;
    this.errorMessage = null;
    this.rateLimitMessage = null;

    this.controller.sendSmsCode().subscribe({
      next: (res) => {
        this.isSending = false;
        if (res.status === 'sent') {
          this.infoMessage = res.message ?? 'Ein Code wurde gesendet.';
        }
        if (res.status === 'rate_limited') {
          this.rateLimitMessage = 'Zu viele SMS-Codes angefordert. Bitte versuchen Sie es in 15 Minuten erneut.';
        }
        this.syncRateLimit();
      },
      error: (err) => {
        this.isSending = false;
        this.errorMessage = err?.message ?? 'Technischer Fehler beim Versand.';
      }
    });
  }

  get isRateLimited(): boolean {
    const blockedUntil = this.flowState.snapshot.smsBlockedUntil;
    if (!blockedUntil) {
      return false;
    }
    return new Date(blockedUntil).getTime() > Date.now();
  }

  get resendDisabled(): boolean {
    return this.isSending || this.isRateLimited;
  }

  back() {
    this.router.navigate(['/patient']);
  }

  private syncRateLimit(sendInitialCode = false) {
    this.controller.getSmsMeta().subscribe({
      next: (meta) => {
        const blockedUntil = meta.smsBlockedUntil ?? null;
        const lastSent = meta.smsLastSentAt ?? null;
        const resendCount = meta.smsResendCount ?? 0;

        this.flowState.setSmsRateLimit(blockedUntil, resendCount, lastSent);

        if (blockedUntil) {
          this.startCountdown(blockedUntil);
        } else {
          this.remainingSeconds = 0;
          this.rateLimitMessage = null;
        }

        if (this.isRateLimited) {
          this.rateLimitMessage = 'Zu viele SMS-Codes angefordert. Bitte versuchen Sie es in 15 Minuten erneut.';
        }

        if (sendInitialCode && !lastSent) {
          this.sendCode();
        }
      },
      error: () => {
        if (sendInitialCode && !this.flowState.snapshot.smsLastSentAt) {
          this.sendCode();
        }
      }
    });
  }

  private startCountdown(blockedUntil: string) {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
    const update = () => {
      const diff = Math.max(0, Math.floor((new Date(blockedUntil).getTime() - Date.now()) / 1000));
      this.remainingSeconds = diff;
      if (diff <= 0 && this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.rateLimitMessage = null;
      }
    };
    update();
    this.countdownTimer = window.setInterval(update, 1000);
  }

  private maskPhone(phone: string): string {
    const digits = phone.replace(/\s+/g, '');
    if (digits.length <= 3) {
      return digits;
    }
    return `${digits.slice(0, 2)}***${digits.slice(-2)}`;
  }
}
